import React from 'react';
import { useDataFetching } from '../../hooks/useDataFetching';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import StatCard from '../shared/StatCard';
import { formatRelativeTime } from '../../utils/formatters';
import { Plus, Play, Pause } from 'lucide-react';

const DetectionRules = () => {
  const { data: rules, loading, error, refetch } = useDataFetching(
    async () => {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/detection-rules`);
      if (!response.ok) throw new Error('Failed to fetch detection rules');
      return response.json();
    },
    []
  );

  if (loading) return <LoadingState message="Loading detection rules..." />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const activeRules = rules?.filter(r => r.status === 'active').length || 0;
  const totalTriggers = rules?.reduce((sum, r) => sum + r.trigger_count, 0) || 0;
  const criticalRules = rules?.filter(r => r.severity === 'Critical').length || 0;

  const getSeverityColor = (severity) => {
    if (severity === 'Critical') return 'bg-red-100 text-red-800';
    if (severity === 'High') return 'bg-orange-100 text-orange-800';
    if (severity === 'Medium') return 'bg-yellow-100 text-yellow-800';
    return 'bg-blue-100 text-blue-800';
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Detection Rules</h1>
          <p className="mt-2 text-gray-600">
            Configure and manage anomaly detection rules for your services
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Rules"
            value={rules?.length || 0}
            subtitle="Configured"
          />
          <StatCard
            title="Active Rules"
            value={activeRules}
            subtitle="Currently monitoring"
          />
          <StatCard
            title="Total Triggers"
            value={totalTriggers}
            subtitle="Last 7 days"
          />
          <StatCard
            title="Critical Rules"
            value={criticalRules}
            subtitle="High priority"
          />
        </div>

        {/* Action Button */}
        <div className="mb-6 flex justify-end">
          <button
            data-testid="new-rule-btn"
            className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Detection Rule
          </button>
        </div>

        {/* Rules Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Detection Rules</h2>
            <p className="text-sm text-gray-600 mt-1">Manage conditions and thresholds for anomaly detection</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rule Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Condition
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Severity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Triggers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Triggered
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rules && rules.map((rule) => (
                  <tr key={rule.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{rule.name}</div>
                      <div className="text-sm text-gray-500">{rule.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">{rule.condition}</code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getSeverityColor(rule.severity)}`}>
                        {rule.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(rule.status)}`}>
                        {rule.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {rule.trigger_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatRelativeTime(rule.last_triggered)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-blue-600 hover:text-blue-900 mr-3">
                        Edit
                      </button>
                      <button className="text-gray-600 hover:text-gray-900">
                        {rule.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetectionRules;